import React, { useState, useRef, useCallback, useEffect } from "react";
import { Photo, TimePoint } from "../types";
import ImageViewer from "./ImageViewer";
import VirtualizedGallery, {
  VirtualizedGalleryApi,
} from "./VirtualizedGallery";
import Timeline from "./Timeline";
import "../styles/EventGallery.css";
import { Language, galleryTranslations } from "../translations";

interface GalleryProps {
  photos: Photo[];
  timePoints: TimePoint[];
  isLoading: boolean;
  showFullDate: boolean;
  onIsFirstChange?: (isFirst: boolean) => void;
  showDownloadButton?: boolean;
  showTimeline?: boolean;
  language?: Language;
  selectedPhotoId?: string | null;
  onPhotoSelect?: (photoId: string | null) => void;
}

const Gallery: React.FC<GalleryProps> = ({
  photos,
  timePoints,
  isLoading,
  showFullDate,
  onIsFirstChange = () => {},
  showDownloadButton = false,
  showTimeline = true,
  language = "en",
  selectedPhotoId = null,
  onPhotoSelect = () => {},
}) => {
  const translations = galleryTranslations[language];
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const virtualizedGalleryApi = useRef<VirtualizedGalleryApi>(null);

  // Handle opening viewer when selectedPhotoId changes (from query param)
  useEffect(() => {
    if (selectedPhotoId && photos.length > 0) {
      const photoIndex = photos.findIndex(
        (photo) => photo.id === selectedPhotoId,
      );
      if (photoIndex !== -1) {
        setCurrentPhotoIndex(photoIndex);
        setViewerOpen(true);
      }
    }
  }, [selectedPhotoId, photos]);

  // Update current time when visible photos change
  const handleVisiblePhotoChange = useCallback(
    (timestamp: string, isFirst: boolean) => {
      setCurrentDateTime(timestamp);
      onIsFirstChange(isFirst);
    },
    [onIsFirstChange],
  );

  // Scroll to a specific position in the gallery
  const scrollToPosition = useCallback(
    (position: number) => {
      if (photos.length === 0) return;

      // Find the corresponding photo by position
      if (position >= 0 && position < photos.length) {
        // The actual scrolling is handled by VirtualizedGallery component internally
        virtualizedGalleryApi.current?.scrollToPhoto(position);
      }
    },
    [photos],
  );

  // Handle opening the image viewer
  const handlePhotoClick = useCallback(
    (index: number) => {
      setCurrentPhotoIndex(index);
      setViewerOpen(true);
      if (photos[index]) {
        onPhotoSelect(photos[index].id);
      }
    },
    [photos, onPhotoSelect],
  );

  // Handle closing the image viewer
  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    onPhotoSelect(null);
  }, [onPhotoSelect]);

  // Handle navigation between photos in the viewer
  const handleNavigate = useCallback(
    (index: number) => {
      setCurrentPhotoIndex(index);
      if (photos[index]) {
        onPhotoSelect(photos[index].id);
      }
    },
    [photos, onPhotoSelect],
  );

  // Handle downloading a photo
  const handleDownloadPhoto = useCallback(
    (url: string, filename: string) => {
      if (!showDownloadButton) return;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    [showDownloadButton],
  );

  return (
    <>
      <div className="gallery-container">
        <div
          className="gallery-wrapper"
          ref={galleryContainerRef}
          style={{ width: "100%" }}
        >
          <VirtualizedGallery
            photos={photos}
            onPhotoClick={handlePhotoClick}
            onVisiblePhotoChange={handleVisiblePhotoChange}
            ref={virtualizedGalleryApi}
            showDownloadButton={showDownloadButton}
            onDownloadPhoto={handleDownloadPhoto}
            language={language}
          />
        </div>

        {showTimeline && (
          <Timeline
            timePoints={timePoints}
            onTimeClick={scrollToPosition}
            currentDateTime={currentDateTime}
            showFullDate={showFullDate}
          />
        )}
      </div>

      <div
        className="loading-indicator"
        style={{
          display: isLoading ? "flex" : "none",
        }}
      >
        {isLoading ? translations.loadingText : translations.loadingText}
      </div>

      {/* Render the image viewer when open */}
      {viewerOpen && photos.length > 0 && (
        <ImageViewer
          photos={photos}
          currentIndex={currentPhotoIndex}
          onClose={handleCloseViewer}
          onNavigate={handleNavigate}
          showDownloadButton={showDownloadButton}
          onDownloadPhoto={handleDownloadPhoto}
          language={language}
        />
      )}
    </>
  );
};

export default Gallery;
